/**
 * Plays a use case against the API, step by step, as the personas it names.
 * Every run is safe to repeat beside other people: it asks for a fresh
 * far-future `{{day}}` (2031–2035, one this browser has not used), suffixes
 * relay names with `{{run}}`, and treats Worlds and occupiers as things to
 * ensure, not to make twice. Before anything is sent it plans: which Worlds it
 * would create against the plan's cap, which account steps need a yes, which
 * personas have no key. A step whose answer is not the one expected is
 * "diverged": the run yields, and whoever is iterating decides whether to go on.
 */

import type { Args, CallRecord, CreateRunner, Json, Persona, RunPlan, Step, StepEvent, UseCase } from '../contracts.ts';
import { holdsSecret, matches, MissingVar, pick, substitute } from './format.ts';
import { accountOps } from './vet.ts';
import { objectOf } from '../model/json.ts';

/** Between steps at pace 1. */
export const STEP_MS = 700;
const FIRST_YEAR = 2031;
const LAST_YEAR = 2035;
const DAYS_KEY = 'schedule-bit.playground.days';
/** World caps by plan, for when `usage` cannot say (it answers 501 where nothing is metered). */
const MAX_WORLDS: Readonly<Record<string, number>> = { beta: 25, starter: 250, scale: 5_000, internal: 100_000 };

/** Days this browser has run on; per viewer, only a convenience, so storage may fail freely. */
const usedDays = new Set<string>();
const loadDays = (): void => {
  try {
    const raw = globalThis.localStorage?.getItem(DAYS_KEY);
    if (raw) for (const d of JSON.parse(raw) as string[]) usedDays.add(d);
  } catch { /* no storage: this tab's memory is enough */ }
};
const saveDays = (): void => {
  try {
    globalThis.localStorage?.setItem(DAYS_KEY, JSON.stringify([...usedDays].slice(-2000)));
  } catch { /* no storage */ }
};

const isoDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** A day in 2031–2035 not used before in this browser. */
export const freshDay = (random: () => number = Math.random): string => {
  loadDays();
  const from = Date.UTC(FIRST_YEAR, 0, 1);
  const span = Math.round((Date.UTC(LAST_YEAR, 11, 31) - from) / 86_400_000) + 1;
  for (let tries = 0; tries < 10_000; tries += 1) {
    const day = isoDay(from + Math.floor(random() * span) * 86_400_000);
    if (usedDays.has(day)) continue;
    usedDays.add(day);
    saveDays();
    return day;
  }
  throw new Error('every day from 2031 to 2035 has been used in this browser');
};

/** The variables every run provides, with `day±N` and `now±N` worked out on asking. */
const builtins = (day: string, run: string, workspaces: Readonly<Partial<Record<Persona, string>>>, now: () => number) => {
  const base = Date.parse(`${day}T00:00:00Z`);
  const [y, m, d] = day.split('-').map(Number);
  return (name: string): Json | undefined => {
    if (name === 'day') return day;
    if (name === 'run') return run;
    if (name === 'year') return y!;
    if (name === 'month0') return m! - 1;
    if (name === 'dom') return d!;
    if (name === 'A' || name === 'B' || name === 'C') return workspaces[name];
    const dayShift = /^day([+-]\d+)$/.exec(name);
    if (dayShift) return isoDay(base + Number(dayShift[1]) * 86_400_000);
    const nowShift = /^now([+-]\d+)$/.exec(name);
    if (nowShift) return now() + Number(nowShift[1]);
    return undefined;
  };
};


/** How long to wait between steps, stopping early when the run is stopped. */
const pause = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise((resolve) => {
    if (ms <= 0 || signal.aborted) return resolve();
    const id = setTimeout(done, ms);
    function done() { clearTimeout(id); signal.removeEventListener('abort', done); resolve(); }
    signal.addEventListener('abort', done);
  });

/** Worlds a step makes: `createWorld`'s name, per persona. */
const createsWorld = (s: Step): string | null => {
  if (s.op !== 'createWorld') return null;
  const name = objectOf(s.args.body as Json).name;
  return typeof name === 'string' ? name : null;
};

export const createRunner: CreateRunner = (deps) => {
  const { client, catalogue, session, vet } = deps;
  const now = deps.now ?? Date.now;

  const auto = { kind: 'auto' } as const;
  const bodyOf = (record: CallRecord): Json | undefined => record.answer?.body;
  const okStatus = (record: CallRecord): boolean => !!record.answer && record.answer.status >= 200 && record.answer.status < 300;

  /** Each persona's workspace, from `whoami`; persona letters stand in when it cannot say. */
  const workspacesOf = async (personas: readonly Persona[]): Promise<Partial<Record<Persona, string>>> => {
    const out: Partial<Record<Persona, string>> = {};
    for (const p of personas) {
      const me = await client.call('whoami', {}, { persona: p, source: auto });
      const ws = objectOf(bodyOf(me)).workspace;
      if (okStatus(me) && typeof ws === 'string') out[p] = ws;
    }
    return out;
  };

  /** Worlds a persona has, and how many it may have. */
  const budgetOf = async (persona: Persona): Promise<{ names: Set<string>; max: number; used: number }> => {
    const list = await client.call('listWorlds', {}, { persona, source: auto });
    const worlds = objectOf(bodyOf(list)).worlds;
    const names = new Set(Array.isArray(worlds) ? worlds.map((w) => objectOf(w).name).filter((n): n is string => typeof n === 'string') : []);
    const usage = await client.call('usage', {}, { persona, source: auto });
    const plan = objectOf(objectOf(bodyOf(usage)).plan);
    const limits = objectOf(plan.limits);
    if (okStatus(usage) && typeof limits.maxWorlds === 'number') {
      const counted = objectOf(objectOf(bodyOf(usage)).usage).worlds;
      return { names, max: limits.maxWorlds, used: typeof counted === 'number' ? Math.max(counted, names.size) : names.size };
    }
    const me = await client.call('whoami', {}, { persona, source: auto });
    const id = objectOf(objectOf(bodyOf(me)).plan).id;
    return { names, max: (typeof id === 'string' && MAX_WORLDS[id]) || MAX_WORLDS.beta!, used: names.size };
  };

  const plan = async (uc: UseCase): Promise<RunPlan & { workspaces: Partial<Record<Persona, string>> }> => {
    const refused: string[] = [];
    const vetted = vet(uc, catalogue());
    if (!vetted.ok) refused.push(...vetted.reasons);
    const held = session.personas();
    const wanted = [...new Set([...uc.personas, ...uc.steps.map((s) => s.as ?? 'A')])];
    const personasMissing = wanted.filter((p) => !held.includes(p));
    for (const p of personasMissing) refused.push(`persona ${p} has no key in this tab`);
    const account = accountOps(uc, catalogue());
    const present = wanted.filter((p) => held.includes(p));
    const workspaces = await workspacesOf(present);
    const newWorlds: string[] = [];
    const reusedWorlds: string[] = [];
    for (const p of present) {
      const names = [...new Set(uc.steps.filter((s) => (s.as ?? 'A') === p).map(createsWorld).filter((n): n is string => n !== null))];
      if (names.length === 0) continue;
      // World names never hold {{variables}} that change per run, so the plan can name them.
      const lookup = builtins('2031-01-01', 'plan', workspaces, now);
      const resolved = names.map((n) => {
        try { return String(substitute(n, (v) => uc.vars?.[v] ?? lookup(v))); } catch { return n; }
      });
      const budget = await budgetOf(p);
      const label = (n: string) => `${workspaces[p] ?? p}/${n}`;
      const fresh = resolved.filter((n) => !budget.names.has(n));
      newWorlds.push(...fresh.map(label));
      reusedWorlds.push(...resolved.filter((n) => budget.names.has(n)).map(label));
      if (budget.used + fresh.length > budget.max) {
        refused.push(`persona ${p}: this run would make ${fresh.length} new World${fresh.length === 1 ? '' : 's'}, and ${budget.used} of ${budget.max} are used`);
      }
    }
    return { newWorlds, reusedWorlds, accountOps: account, personasMissing, refused, workspaces };
  };

  /** The step's call, and when `ensure` lets an existing thing stand in for a new one, the answer that counts. */
  const perform = async (s: Step, args: Args, persona: Persona, source: CallRecord['source']): Promise<{ record: CallRecord; answer: { status: number; body: Json; head: string | null } | null; ensured: boolean }> => {
    // Pinned: a step acts as the persona it names, and a World key naming another persona's World is refused, not followed.
    const opts = { persona, source, pin: true };
    const wanted = objectOf(args.body as Json);
    if (s.ensure && s.op === 'declareOccupier') {
      // Events and series may share a name, so look first rather than make a second.
      const list = await client.call('listOccupiers', { w: args.w ?? null }, opts);
      const found = (objectOf(bodyOf(list)).occupiers as Json[] | undefined ?? []).map(objectOf).find((o) =>
        o.name === wanted.name && o.kind === (wanted.kind ?? 'holder') && (wanted.holder === undefined || o.holder === wanted.holder));
      if (found) return { record: list, answer: { status: 201, body: { occupier: found }, head: null }, ensured: true };
    }
    const record = await client.call(s.op, args, opts);
    const status = record.answer?.status ?? 0;
    if (s.ensure && s.op === 'createWorld' && status === 409) {
      const got = await client.call('getWorld', { w: wanted.name ?? null }, opts);
      const world = objectOf(bodyOf(got));
      const same = okStatus(got)
        && (world.preset === (wanted.preset ?? 'temporal-hour'))
        && (world.allowDisplace === (wanted.allowDisplace ?? false))
        && (world.tieBreak === (wanted.tieBreak ?? 'incumbent'));
      if (same) return { record, answer: { status: 201, body: world, head: null }, ensured: true };
    }
    if (s.ensure && s.op === 'setCapacity' && status === 400) {
      const got = await client.call('getWorld', { w: args.w ?? null }, opts);
      const seats = objectOf(bodyOf(got)).seats;
      if (typeof seats === 'number' && typeof wanted.seats === 'number' && seats >= wanted.seats) {
        return { record, answer: { status: 200, body: { seats }, head: null }, ensured: true };
      }
    }
    return { record, answer: record.answer ? { status: record.answer.status, body: record.answer.body, head: record.answer.head } : null, ensured: false };
  };

  async function* run(uc: UseCase, opts: Parameters<ReturnType<CreateRunner>['run']>[1]): AsyncGenerator<StepEvent> {
    const planned = await plan(uc);
    if (planned.refused.length > 0) {
      yield { kind: 'refused', reasons: planned.refused };
      return;
    }
    if (planned.accountOps.length > 0 && !(await opts.confirmAccount(planned.accountOps))) {
      yield { kind: 'refused', reasons: [`not confirmed: ${planned.accountOps.join(', ')}`] };
      return;
    }
    const day = freshDay();
    const runId = `${(now() % 1_679_616).toString(36).padStart(4, '0')}${Math.floor(Math.random() * 1296).toString(36).padStart(2, '0')}`;
    const lookupBuiltin = builtins(day, runId, planned.workspaces, now);
    const saved = new Map<string, Json>();
    const lookup = (name: string, depth = 0): Json | undefined => {
      if (saved.has(name)) return saved.get(name);
      const declared = uc.vars?.[name];
      if (declared !== undefined) {
        if (depth > 8) return declared;
        return substitute(declared, (n) => lookup(n, depth + 1));
      }
      return lookupBuiltin(name);
    };

    let passed = 0;
    let failed = 0;
    for (const [index, raw] of uc.steps.entries()) {
      if (opts.signal.aborted) break;
      if (opts.beforeStep) {
        let ahead: Args | undefined;
        try { ahead = substitute(raw.args as Json, (n) => lookup(n)) as Args; } catch { ahead = undefined; }
        await opts.beforeStep(raw, index, runId, ahead);
      }
      if (opts.signal.aborted) break;
      const persona = raw.as ?? 'A';
      let args: Args;
      let expect: Json | undefined;
      try {
        args = substitute(raw.args as Json, (n) => lookup(n)) as Args;
        expect = raw.expect?.body === undefined ? undefined : substitute(raw.expect.body, (n) => lookup(n));
      } catch (e) {
        failed += 1;
        yield { kind: 'diverged', index, why: e instanceof MissingVar ? `${e.message}: an earlier step did not save it` : String(e) };
        continue;
      }
      const { record, answer, ensured } = await perform(raw, args, persona, { kind: 'scenario', run: runId, step: index });
      const statuses = raw.expect ? (Array.isArray(raw.expect.status) ? raw.expect.status : [raw.expect.status]) : null;
      const status = answer?.status ?? 0;
      const diff: Json[] = [];
      if (statuses ? !statuses.includes(status) : status === 0 || status >= 500) {
        diff.push({ at: 'status', want: statuses ?? '< 500', got: status });
      } else if (expect !== undefined) {
        for (const m of matches(expect, answer?.body)) diff.push({ at: m.at, want: m.want, got: m.got });
      }
      const whole: Json = { status, body: answer?.body ?? null, head: answer?.head ?? null };
      for (const [name, path] of Object.entries(raw.save ?? {})) {
        const value = pick(whole, path);
        if (value === undefined) diff.push({ at: `save ${name}`, want: path, got: null });
        // A secret is shown once and never kept, whatever path reached it (a whole object, a tool's text).
        else if (holdsSecret(value)) diff.push({ at: `save ${name}`, want: 'no secret', got: '(a secret, not kept)' });
        else saved.set(name, value);
      }
      const ok = diff.length === 0;
      if (ok) passed += 1;
      else failed += 1;
      yield { kind: 'step', index, record, ok, ...(ok ? (ensured ? { diff: { ensured: true } } : {}) : { diff }) };
      if (!ok) yield { kind: 'diverged', index, why: `step ${index + 1} (${raw.op}) did not answer as expected` };
      if (index < uc.steps.length - 1 && opts.pace > 0) await pause(STEP_MS / opts.pace, opts.signal);
    }
    yield { kind: 'done', passed, failed };
  }

  return {
    plan: async (uc) => {
      const { workspaces: _w, ...rest } = await plan(uc);
      return rest;
    },
    run,
  };
};
